import {
  createWorkerShoppableQualificationRequest,
  type GetWorkerShoppableQualificationResponse,
  getWorkerShoppableQualificationResponse,
} from "@clipboard-health/contract-backend-main";
import { API_TYPES } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { generatePath } from "react-router-dom";

import { invalidateShifts } from "../../invalidateShifts";

const CREATE_SHOPPABLE_QUALIFICATION_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/workers/:workerId/shoppable-qualifications`;

interface UseCreateShoppableQualificationParams {
  workerId: string;
  qualificationId: string;
}

export function useCreateShoppableQualification(
  options?: UseMutationOptions<
    GetWorkerShoppableQualificationResponse,
    AxiosError<GetWorkerShoppableQualificationResponse>,
    UseCreateShoppableQualificationParams
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UseCreateShoppableQualificationParams) => {
      const response = await post({
        url: generatePath(CREATE_SHOPPABLE_QUALIFICATION_URL, {
          workerId: params.workerId,
        }),
        data: {
          data: {
            type: API_TYPES.workerShoppableQualification,
            relationships: {
              qualification: {
                data: {
                  type: "qualifications",
                  id: params.qualificationId,
                },
              },
            },
          },
        },
        requestSchema: createWorkerShoppableQualificationRequest,
        responseSchema: getWorkerShoppableQualificationResponse,
      });

      return response.data;
    },
    onSuccess: (data) => {
      const { data: shoppableQualification } = data;

      queryClient.setQueryData([GET_WORKER_QUERY_KEY], (oldData: Worker | undefined) => {
        if (!isDefined(oldData)) {
          return oldData;
        }

        return {
          ...oldData,
          shoppableQualifications: [
            ...oldData.shoppableQualifications,
            {
              ...shoppableQualification,
              relationships: {
                ...shoppableQualification.relationships,
                qualification: {
                  data: {
                    ...shoppableQualification.relationships.qualification.data,
                    // we migrated `qualification` object to have singular `qualifications` type in worker-bff api so it's still incompatible with the contract-backend-main
                    type: API_TYPES.qualification,
                  },
                },
              },
            },
          ],
        };
      });

      // need to invalidate shifts to get the new roles in the shifts list
      void invalidateShifts(queryClient);
    },
    ...options,
  });
}
